import moment from 'moment';

const HISTORY_LABELS = {
    role: { doctor: 'Bác sĩ', sales: 'Nhân viên Sales', assistant: 'Phụ tá' },
    component: {
        full_service: 'Toàn bộ dịch vụ (dữ liệu cũ)',
        examination: 'Khám',
        treatment: 'Điều trị',
        implant_placement: 'Cắm trụ',
        prosthetic_restoration: 'Phục hình',
        apprentice: 'Học việc',
    },
    reason: {
        CASE_FAILED: 'Ca không thành công', EMPLOYEE_LEFT: 'Nhân sự nghỉ việc/ngừng phụ trách',
        NORMAL_HANDOVER: 'Chuyển giao thông thường', INTERNAL_TRANSFER: 'Điều chuyển nội bộ',
        SPECIALTY_CHANGE: 'Chuyển giao theo chuyên môn', OTHER: 'Thay đổi từ dữ liệu thực tế',
    },
    changeType: { ASSIGN: 'Phân công nhân sự', END: 'Kết thúc phân công', HANDOVER: 'Chuyển giao nhân sự' },
    failureStatus: { REPORTED: 'Đã báo cáo – dữ liệu cũ chờ xử lý', CONFIRMED: 'Đã ghi nhận và gửi điều chỉnh hoa hồng' },
    adjustmentStatus: { PENDING: 'Chờ duyệt', APPROVED: 'Đã ghi nhận', REJECTED: 'Đã từ chối', CANCELLED: 'Đã hủy' },
};

const getCatalogLabel = (catalog, keyName, value) => ((catalog || []).find(item => item?.[keyName] === value)?.label || '');
const getHistoryLabel = (type, catalog, keyName, value, emptyLabel) => (
    HISTORY_LABELS[type]?.[value] || getCatalogLabel(catalog, keyName, value) || emptyLabel
);

export const getStaffHistoryLabels = ({ roles = [], components = [], reasons = [] } = {}) => ({
    role: value => getHistoryLabel('role', roles, 'role_key', value, 'Vai trò chưa xác định'),
    component: value => getHistoryLabel('component', components, 'component_key', value, 'Phạm vi chưa xác định'),
    reason: value => getHistoryLabel('reason', reasons, 'reason_code', value, 'Lý do chưa xác định'),
    changeType: value => getHistoryLabel('changeType', [], '', value, 'Cập nhật phân công nhân sự'),
    failureStatus: value => getHistoryLabel('failureStatus', [], '', value, 'Trạng thái ca không thành công chưa xác định'),
    adjustmentStatus: value => getHistoryLabel('adjustmentStatus', [], '', value, 'Chưa xác định'),
});

export const getAssignmentEmployeeName = assignment => assignment?.employee?.full_name || 'Chưa xác định nhân sự';

export const formatStaffHistoryDateTime = value => {
    if (!value) return 'Chưa xác định thời gian';
    const parsedValue = moment(value, [moment.ISO_8601, 'YYYY-MM-DD HH:mm:ss'], true);
    return parsedValue.isValid() ? parsedValue.format('HH:mm [ngày] DD/MM/YYYY') : 'Chưa xác định thời gian';
};

export const sortStaffHistory = history => [...(Array.isArray(history) ? history : [])].sort((left, right) => {
    const timeDifference = moment(right?.effective_at).valueOf() - moment(left?.effective_at).valueOf();
    return timeDifference || Number(right?.id || 0) - Number(left?.id || 0);
});

const historyEmployeeId = assignment => assignment?.employee?.id || assignment?.employee_id || '';
const staffHistoryRowSignature = row => [row?.change_type, row?.role_key, row?.component_key,
    historyEmployeeId(row?.from_assignment), historyEmployeeId(row?.to_assignment), row?.effective_at,
    row?.reason_code, (row?.note || '').trim()].join('|');

export const getStaffHistoryRoleLabel = (row, labels) => {
    const roleLabel = labels.role(row?.role_key);
    const componentLabel = labels.component(row?.component_key);
    return row?.role_key === 'doctor' && row?.component_key && row.component_key !== 'full_service'
        ? `${roleLabel} ${componentLabel.toLocaleLowerCase('vi-VN')}` : roleLabel;
};

export const getStaffHistoryAction = row => {
    if (row?.display_change_type === 'REPLACE') return { label: 'Thay thế', color: 'blue' };
    if (row?.change_type === 'ASSIGN') return { label: 'Thêm mới', color: 'green' };
    if (row?.change_type === 'END') return { label: 'Xóa nhân sự', color: 'red' };
    if (row?.change_type === 'HANDOVER') return { label: 'Chuyển giao', color: 'orange' };
    return { label: 'Cập nhật', color: 'gray' };
};

// Một lần lưu có thể sinh nhiều dòng audit; gom lại để Timeline phản ánh đúng một lần thao tác.
export const buildStaffHistoryTimeline = history => {
    const uniqueRows = [];
    const signatures = new Set();
    sortStaffHistory(history).forEach(row => {
        const signature = staffHistoryRowSignature(row);
        if ((!historyEmployeeId(row?.from_assignment) && !historyEmployeeId(row?.to_assignment)) || signatures.has(signature)) return;
        signatures.add(signature);
        uniqueRows.push(row);
    });

    const groups = [];
    uniqueRows.forEach(row => {
        const key = [row?.effective_at || '', row?.actor_id || row?.actor?.id || '', row?.created_at || ''].join('|');
        let group = groups.find(item => item.key === key);
        if (!group) {
            group = { key: key || String(row?.id), effectiveAt: row?.effective_at, actor: row?.actor, rows: [] };
            groups.push(group);
        }
        group.rows.push(row);
    });

    groups.forEach(group => {
        const consumed = new Set();
        const displayRows = [];
        const rowIndexes = group.rows.map((row, index) => ({ row, index }))
            .sort((left, right) => (left.row?.change_type === 'END' ? -1 : 0) - (right.row?.change_type === 'END' ? -1 : 0));
        rowIndexes.forEach(({ row, index }) => {
            if (consumed.has(index)) return;
            if (row?.change_type === 'END') {
                const replacementIndex = group.rows.findIndex((candidate, candidateIndex) => candidateIndex !== index
                    && !consumed.has(candidateIndex) && candidate?.change_type === 'ASSIGN'
                    && candidate?.role_key === row?.role_key && candidate?.component_key === row?.component_key);
                if (replacementIndex >= 0) {
                    consumed.add(replacementIndex);
                    displayRows.push({ ...row, display_change_type: 'REPLACE', to_assignment: group.rows[replacementIndex].to_assignment });
                    return;
                }
            }
            displayRows.push(row);
        });
        group.rows = displayRows;
    });
    return groups;
};
